// ISDP CUSTOM: `ui:field: AdoRepoTarget` — where the new docs repository goes. Value:
// { project, repoName }.
//
// The Azure DevOps PAT is entered on the same step by the built-in `ui:field: Secret` (secret key
// `adoPat`) and read here from the template secrets — it is only ever sent to dev.azure.com from the
// browser, never logged, never put in this field's value. With a PAT present (debounced) the field
// lists the user's projects in ONE searchable combobox (type to filter, arrow keys + Enter to pick;
// loading / no-match / error shown inline), and live-checks that the repo name is free both in that
// ADO project and in the DevHub catalog (component:default/<name>; two repos with the same entity name
// shadow each other org-wide).
//
// Repository name: the user types the BASE; a fixed "-docs" suffix shows in the input and is appended
// for them (not when the base already ends in "docs" — no "manual-docs-docs"). The field value keeps
// the FINAL name, and the pattern/length rules and live checks run on that.
//
// ui:options (all optional): secretKey (default 'adoPat'), titleField (formData key the default base
// `slug(title)` minus any trailing "docs" is derived from, default 'title').
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  useTemplateSecrets,
  type CustomFieldValidator,
  type FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { devsecopsMessages } from '../../../i18n/devsecops';
import { useDebouncedValue } from '../../search/useDebouncedValue';
import {
  DEFAULT_PAT_SECRET,
  DEFAULT_TITLE_FIELD,
  adoHeaders,
  adoProjectsUrl,
  adoRepoUrl,
  adoRepoTargetShapeErrors,
  availabilityError,
  baseFromRepoName,
  baseHasDocsEnding,
  classifyAdoStatus,
  combineAvailability,
  defaultRepoBase,
  finalRepoName,
  isValidRepoName,
  lookupAvailability,
  mapProjectsResponse,
  normalizeBaseInput,
  recordAvailability,
  type AdoErrorKind,
  type AdoRepoTargetError,
  type AdoRepoTargetValue,
  type RepoAvailability,
  ADO_LICENSE_ORDER_URL,
  ADO_PROJECT_REQUEST_URL,
} from './fieldHelpers';
import { validatorMessages, type FieldMessageKey } from './fieldMessages';

export type AdoRepoTargetUiOptions = {
  secretKey?: string;
  titleField?: string;
};

export const ADO_ERROR_KEYS: Record<AdoRepoTargetError, FieldMessageKey> = {
  projectRequired: 'fields.projectRequired',
  repoRequired: 'fields.repoRequired',
  repoPattern: 'fields.repoPattern',
  repoTakenAdo: 'fields.repoTakenAdo',
  repoTakenCatalog: 'fields.repoTakenCatalog',
  repoChecking: 'fields.repoStillChecking',
  repoCheckFailed: 'fields.repoCheckFailed',
};

type ProjectsState =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready'; items: string[] }
  | { state: 'error'; kind: AdoErrorKind; status?: number };

// Never the Backstage fetchApi: that would attach the DevHub token to a third-party request.
const adoFetch = (url: string, pat: string) =>
  window.fetch(url, {
    headers: adoHeaders(pat),
    credentials: 'omit',
    cache: 'no-store',
  });

export const AdoRepoTargetField = (
  props: FieldExtensionComponentProps<
    AdoRepoTargetValue,
    AdoRepoTargetUiOptions
  >,
) => {
  const {
    formData,
    onChange,
    rawErrors,
    required,
    disabled,
    schema,
    uiSchema,
    idSchema,
    formContext,
  } = props;
  const options = uiSchema?.['ui:options'] ?? {};
  const secretKey = options.secretKey || DEFAULT_PAT_SECRET;
  const titleField = options.titleField || DEFAULT_TITLE_FIELD;
  const { t } = useTranslationRef(devsecopsMessages);
  const tt = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const catalogApi = useApi(catalogApiRef);
  const { secrets } = useTemplateSecrets();
  const pat = useDebouncedValue((secrets[secretKey] ?? '').trim(), 500);

  const value: AdoRepoTargetValue = {
    project: formData?.project ?? '',
    repoName: formData?.repoName ?? '',
  };
  const latest = useRef(value);
  latest.current = value;
  const update = useCallback(
    (patch: Partial<AdoRepoTargetValue>) =>
      onChange({ ...latest.current, ...patch }),
    [onChange],
  );

  // ---- repository name: the user edits the base, the value holds the final "<base>-docs" ----
  const [base, setBaseState] = useState(() => baseFromRepoName(value.repoName));
  const setBase = useCallback(
    (next: string) => {
      setBaseState(next);
      update({ repoName: finalRepoName(next) });
    },
    [update],
  );
  // Default base from the template title, until the user edits the name themselves.
  const title = (
    formContext as { formData?: Record<string, unknown> } | undefined
  )?.formData?.[titleField];
  const suggested = defaultRepoBase(typeof title === 'string' ? title : '');
  const lastSuggested = useRef<string>('');
  const baseRef = useRef(base);
  baseRef.current = base;
  useEffect(() => {
    const current = baseRef.current;
    if (
      suggested &&
      (current === '' || current === lastSuggested.current) &&
      current !== suggested
    ) {
      setBase(suggested);
    }
    lastSuggested.current = suggested;
  }, [suggested, setBase]);

  // ---- projects ----
  const [projects, setProjects] = useState<ProjectsState>({ state: 'idle' });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!pat) {
      setProjects({ state: 'idle' });
      return undefined;
    }
    let active = true;
    setProjects({ state: 'loading' });
    adoFetch(adoProjectsUrl(), pat)
      .then(async resp => {
        if (!active) return;
        if (resp.status !== 200) {
          setProjects({
            state: 'error',
            kind: classifyAdoStatus(resp.status),
            status: resp.status,
          });
          return;
        }
        const items = mapProjectsResponse(await resp.json().catch(() => ({})));
        if (active) setProjects({ state: 'ready', items });
      })
      .catch(() => {
        if (active) setProjects({ state: 'error', kind: 'network' });
      });
    return () => {
      active = false;
    };
  }, [pat, reload]);

  // ---- availability (ADO repo + DevHub catalog) ----
  const checkKey = useDebouncedValue(
    `${value.project}\u0000${value.repoName}`,
    400,
  );
  const [availability, setAvailability] = useState<
    RepoAvailability | undefined
  >();
  const [recheck, setRecheck] = useState(0);
  useEffect(() => {
    const [project, repoName] = checkKey.split('\u0000');
    const target = { project, repoName };
    if (!pat || !project || !isValidRepoName(repoName)) {
      setAvailability(undefined);
      return undefined;
    }
    let active = true;
    const checking: RepoAvailability = { state: 'checking' };
    recordAvailability(target, checking);
    setAvailability(checking);
    Promise.all([
      adoFetch(adoRepoUrl(project, repoName), pat).then(
        r => r.status as number | 'network',
        () => 'network' as const,
      ),
      catalogApi.getEntityByRef(`component:default/${repoName}`).then(
        e => !!e,
        () => false,
      ),
    ]).then(([adoStatus, catalogTaken]) => {
      const verdict = combineAvailability(adoStatus, catalogTaken);
      recordAvailability(target, verdict);
      if (active) setAvailability(verdict);
    });
    return () => {
      active = false;
    };
  }, [checkKey, pat, catalogApi, recheck]);

  // ---- rendering ----
  const suffixShown = !baseHasDocsEnding(base);
  const id = idSchema?.$id ?? 'adoRepoTarget';
  const showAll = (rawErrors?.length ?? 0) > 0;
  const [repoTouched, setRepoTouched] = useState(false);
  const shapeErrors = adoRepoTargetShapeErrors(value);
  const repoShapeErr = shapeErrors.find(
    e => e === 'repoRequired' || e === 'repoPattern',
  );
  const projectErr = showAll && shapeErrors.includes('projectRequired');

  // Where people without Azure DevOps access go first (ServiceNow catalogue items).
  const accessLinks = (
    <>
      {' '}
      {tt('fields.adoNewToAdo')}{' '}
      <a
        className="devhub-sf__link"
        href={ADO_LICENSE_ORDER_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {tt('fields.adoOrderLicense')}
      </a>
      {' · '}
      <a
        className="devhub-sf__link"
        href={ADO_PROJECT_REQUEST_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {tt('fields.adoRequestProject')}
      </a>
    </>
  );

  const adoErrorText = (kind: AdoErrorKind, status?: number) => {
    if (kind === 'unauthorized') return tt('fields.adoErrUnauthorized');
    if (kind === 'network') return tt('fields.adoErrNetwork');
    return tt('fields.adoErrHttp', { status: status ?? '?' });
  };

  // One combobox for the project: type-ahead filtering, keyboard navigation, and the loading /
  // empty / error states inline (helper line under the input).
  const items = projects.state === 'ready' ? projects.items : [];
  let projectHelper: JSX.Element | string = ' ';
  let projectHelperError = false;
  if (projects.state === 'idle') {
    projectHelper = tt('fields.adoNeedPat');
  } else if (projects.state === 'loading') {
    projectHelper = tt('fields.adoLoadingProjects');
  } else if (projects.state === 'error') {
    projectHelperError = true;
    projectHelper = (
      <span role="alert">
        {adoErrorText(projects.kind, projects.status)}{' '}
        <button
          type="button"
          className="devhub-sf__link"
          onClick={() => setReload(r => r + 1)}
        >
          {tt('fields.adoRetry')}
        </button>
        {projects.kind === 'unauthorized' && accessLinks}
      </span>
    );
  } else if (items.length === 0) {
    projectHelperError = true;
    projectHelper = (
      <span role="alert">
        {tt('fields.adoNoProjects')}
        {accessLinks}
      </span>
    );
  } else if (projectErr) {
    projectHelperError = true;
    projectHelper = tt('fields.projectRequired');
  } else {
    projectHelper = tt('fields.adoProjectCount', { num: items.length });
  }

  const projectBody = (
    <Autocomplete<string, false, false, false>
      id={`${id}-project`}
      className="devhub-sf__combo"
      options={items}
      value={value.project || null}
      onChange={(_e, next) => update({ project: next ?? '' })}
      disabled={disabled || projects.state === 'idle'}
      loading={projects.state === 'loading'}
      loadingText={tt('fields.adoLoadingProjects')}
      noOptionsText={tt('fields.adoNoFilterMatch')}
      openText={tt('fields.adoOpen')}
      closeText={tt('fields.adoClose')}
      clearText={tt('fields.adoClear')}
      autoHighlight
      openOnFocus
      fullWidth
      renderInput={params => (
        <TextField
          {...params}
          label={tt('fields.adoProject')}
          placeholder={
            items.length ? tt('fields.adoProjectPlaceholder') : undefined
          }
          margin="dense"
          required
          error={projectHelperError}
          helperText={projectHelper}
          FormHelperTextProps={
            { className: 'devhub-sf__helper', component: 'div' } as object
          }
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            spellCheck: false,
          }}
        />
      )}
    />
  );

  // Between a keystroke and the debounced check, the last verdict belongs to a different name.
  const pending =
    !!pat &&
    !!value.project &&
    isValidRepoName(value.repoName) &&
    checkKey !== `${value.project}\u0000${value.repoName}`;
  const shown: RepoAvailability | undefined = pending
    ? { state: 'checking' }
    : availability;

  let repoStatus: JSX.Element | null = null;
  if (repoShapeErr && (repoTouched || showAll)) {
    repoStatus = (
      <span className="devhub-sf__status is-error">
        {tt(ADO_ERROR_KEYS[repoShapeErr])}
      </span>
    );
  } else if (shown?.state === 'checking') {
    repoStatus = (
      <span className="devhub-sf__status is-checking">
        {tt('fields.repoChecking')}
      </span>
    );
  } else if (shown?.state === 'available') {
    repoStatus = (
      <span className="devhub-sf__status is-ok" data-status="available">
        <odx-icon name="core::check" size="sm" /> {tt('fields.repoAvailable')}
      </span>
    );
  } else if (shown?.state === 'taken') {
    repoStatus = (
      <span
        className="devhub-sf__status is-error"
        data-status="taken"
        role="alert"
      >
        {tt(
          shown.where === 'ado'
            ? 'fields.repoTakenAdo'
            : 'fields.repoTakenCatalog',
        )}
      </span>
    );
  } else if (shown?.state === 'error') {
    repoStatus = (
      <span className="devhub-sf__status is-error" role="alert">
        {tt('fields.repoCheckFailed')} {adoErrorText(shown.kind, shown.status)}{' '}
        <button
          type="button"
          className="devhub-sf__link"
          onClick={() => setRecheck(r => r + 1)}
        >
          {tt('fields.adoRetry')}
        </button>
      </span>
    );
  }

  return (
    <FormControl
      component="fieldset"
      fullWidth
      margin="normal"
      required={required}
      className="devhub-sf"
    >
      {schema.title && <FormLabel component="legend">{schema.title}</FormLabel>}
      {schema.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}

      <div className="devhub-sf__group" data-part="project">
        {projectBody}
      </div>

      <div className="devhub-sf__group" data-part="repo">
        <TextField
          id={`${id}-repoName`}
          label={tt('fields.repoName')}
          value={base}
          required
          disabled={disabled}
          fullWidth
          margin="dense"
          autoComplete="off"
          inputProps={{
            spellCheck: false,
            maxLength: 63,
            'aria-describedby': `${id}-repoFinal`,
          }}
          InputProps={{
            endAdornment: suffixShown ? (
              <InputAdornment
                position="end"
                className="devhub-sf__suffix"
                data-testid="repo-suffix"
              >
                -docs
              </InputAdornment>
            ) : undefined,
          }}
          error={!!repoShapeErr && (repoTouched || showAll)}
          helperText={tt('fields.repoHint')}
          onChange={e => setBase(normalizeBaseInput(e.target.value))}
          onBlur={() => {
            setRepoTouched(true);
            // Tidy a trailing hyphen left over from typing ("manual-" → "manual").
            const tidy = base.replace(/-+$/, '');
            if (tidy !== base) setBase(tidy);
          }}
        />
        {value.repoName && (
          <div
            className="devhub-sf__final"
            id={`${id}-repoFinal`}
            data-testid="repo-final"
          >
            {tt('fields.repoWillBe')} <strong>{value.repoName}</strong>
          </div>
        )}
        <div className="devhub-sf__repo-status" aria-live="polite">
          {repoStatus}
        </div>
      </div>
    </FormControl>
  );
};

export const adoRepoTargetValidation: CustomFieldValidator<
  AdoRepoTargetValue,
  AdoRepoTargetUiOptions
> = (value, validation, { apiHolder }) => {
  const msg = validatorMessages(apiHolder);
  const shape = adoRepoTargetShapeErrors(value);
  for (const err of shape) validation.addError(msg(ADO_ERROR_KEYS[err]));
  if (shape.length) return;
  const err = availabilityError(lookupAvailability(value));
  if (err) validation.addError(msg(ADO_ERROR_KEYS[err]));
};
